import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/marketing/components/ui/button";
import { Checkbox } from "@/marketing/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/marketing/components/ui/popover";

export interface ToggleFilterOption {
  key: string;
  label: string;
  active: boolean;
  onToggle: (value: boolean) => void;
}

interface ToggleFilterDropdownProps {
  label: string;
  options: ToggleFilterOption[];
}

export function ToggleFilterDropdown({
  label,
  options,
}: ToggleFilterDropdownProps) {
  const [open, setOpen] = useState(false);

  const activeCount = options.filter((o) => o.active).length;
  const displayLabel = activeCount > 0 ? `${label}: ${activeCount}` : label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="tw-h-10 tw-gap-1 tw-font-normal">
          {displayLabel}
          <ChevronDown className="tw-h-4 tw-w-4 tw-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw-w-52 tw-p-2" align="start">
        <div className="tw-max-h-60 tw-overflow-auto">
          {options.map((option) => (
            <label
              key={option.key}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm hover:tw-bg-accent tw-cursor-pointer"
            >
              <Checkbox
                checked={option.active}
                onCheckedChange={(checked) => option.onToggle(!!checked)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {activeCount > 0 && (
          <div className="tw-border-t tw-pt-2 tw-mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="tw-w-full tw-h-8 tw-text-xs"
              onClick={() =>
                options.forEach((o) => {
                  if (o.active) o.onToggle(false);
                })
              }
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
